import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
    mdiCog,
    mdiContentDuplicate,
    mdiTransitConnectionVariant,
} from '@mdi/js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { TableCell, TableRowMedia } from 'common/components/Table';
import { ITrainingManagementListItemFragment } from 'graphql/types';
import { getUrl } from 'route/utils/getUrl';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { ChipGroup } from 'common/components/Chip';
import { Checkbox } from 'common/components/Checkbox';
import { Icon } from 'common/components/Icon';
import { ItemMedia } from 'common/components/ItemMedia';
import { IconButton } from 'common/components/IconButton';
import { Tooltip } from 'common/components/Tooltip';
import { ExtraTrainingFieldsChips } from 'training/components/ExtraTrainingFieldsChips';
import { TrainingRoleChips } from 'training/components/TrainingRoleChips';
import { TrainingAvailability } from 'training/components/TrainingAvailability';
import { TrainingCopyAction } from 'training/actions/TrainingCopyAction';
import { gotoTrainingPage } from 'training/utils/gotoRoutes';
import { getVisibleTrainingRoles } from 'training/utils/trainingRoles';

interface IProps {
    training: ITrainingManagementListItemFragment;
    isSelected: boolean;
    selectable?: boolean;
    onSelect?: () => void;
}

export const TrainingsManagementTableRow = ({
    training,
    isSelected,
    selectable = false,
    onSelect,
}: IProps) => {
    const [translate] = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { openGlobalDrawer } = useGlobalDrawer();

    const {
        id,
        title,
        overviewImage,
        image,
        isActive,
        startDate,
        endDate,
        trainers,
        groupCount,
        userCount,
        extraTrainingFieldsLabels,
        rolesForCurrentUser,
    } = training;

    const trainingImage = overviewImage?.url || image?.url || '';

    const hasRoleChips = !!getVisibleTrainingRoles(rolesForCurrentUser).length;

    // Only show the first trainer by name to keep the row compact; any
    // remaining trainers are summarized as "and N more".
    const trainerAmount = 1;
    const trainersDisplay = !!trainers.length && (
        <>
            {trainers
                .slice(0, trainerAmount)
                .map((trainer) => trainer.name)
                .join(', ')}
            {trainers.length > trainerAmount &&
                `, ${translate('andMoreResults', {
                    count: trainers.length - trainerAmount,
                })}`}
        </>
    );

    return (
        <TableRowMedia
            hover
            sx={{
                cursor: 'pointer',
                '&:hover .training-row-action, & .training-row-action:focus-visible':
                    {
                        opacity: 1,
                    },
            }}
            onClick={() => navigate(getUrl('TRAINING', { trainingId: id }))}
        >
            {selectable && (
                <TableCell>
                    <Checkbox
                        checked={isSelected}
                        onClick={(event) => {
                            onSelect?.();
                            event.stopPropagation();
                        }}
                    />
                </TableCell>
            )}

            <TableCell sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ItemMedia
                        color="primary"
                        image={trainingImage}
                        size="small"
                    >
                        <Icon
                            path={mdiTransitConnectionVariant}
                            size="2rem"
                            sizeSm="3rem"
                        />
                    </ItemMedia>

                    <Box>
                        {title}

                        {!!trainersDisplay && (
                            <Typography
                                color="text.secondary"
                                sx={{ fontSize: '1.4rem' }}
                                variant="body2"
                            >
                                {trainersDisplay}
                            </Typography>
                        )}

                        {(hasRoleChips ||
                            !!extraTrainingFieldsLabels?.length) && (
                            <ChipGroup sx={{ my: 0.25 }}>
                                <TrainingRoleChips
                                    roles={rolesForCurrentUser}
                                />

                                <ExtraTrainingFieldsChips
                                    extraTrainingFieldsLabels={
                                        extraTrainingFieldsLabels
                                    }
                                />
                            </ChipGroup>
                        )}
                    </Box>
                </Box>
            </TableCell>

            <TableCell>{groupCount}</TableCell>

            <TableCell>{userCount}</TableCell>

            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <TrainingAvailability
                    endDate={endDate}
                    isActive={isActive}
                    startDate={startDate}
                />
            </TableCell>

            <TableCell
                sx={{ width: '1px' }}
                onClick={(event) => event.stopPropagation()}
            >
                <Box sx={{ display: 'flex' }}>
                    <TrainingCopyAction
                        id={id}
                        onSuccess={(newTraining) =>
                            gotoTrainingPage(navigate, newTraining.id)
                        }
                    >
                        {({ copyTraining }) => (
                            <Tooltip title={translate('copy')}>
                                <IconButton
                                    aria-label={translate('copy')}
                                    className="training-row-action"
                                    iconPath={mdiContentDuplicate}
                                    iconSize="2.4rem"
                                    sx={{ opacity: 0 }}
                                    onClick={copyTraining}
                                />
                            </Tooltip>
                        )}
                    </TrainingCopyAction>

                    <Tooltip title={translate('trainingSettings')}>
                        <IconButton
                            aria-label={translate('trainingSettings')}
                            className="training-row-action"
                            iconPath={mdiCog}
                            iconSize="2.4rem"
                            sx={{ opacity: 0 }}
                            onClick={() => {
                                openGlobalDrawer(
                                    {
                                        type: IGlobalDrawerType.TrainingUpdate,
                                        itemId: id,
                                    },
                                    location
                                );
                            }}
                        />
                    </Tooltip>
                </Box>
            </TableCell>
        </TableRowMedia>
    );
};
